import * as React from 'react';
import { cn } from '@/lib/utils';

/** Flight Deck status pill — uppercase mono label with a leading status dot
 * and a tinted ring. The colour is selected by `status`; pulse adds a
 * radiating ring animation used for "live" runtime states (e.g. running). */
export type StatusPillStatus =
  | 'running'
  | 'paused'
  | 'pending'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'live'
  | 'active'
  | 'draft'
  | 'published'
  | 'archived';

const STATUS_STYLES: Record<
  StatusPillStatus,
  { fg: string; bg: string; dot: string; ring: string }
> = {
  running: {
    fg: 'text-accent-bright',
    bg: 'bg-[oklch(0.72_0.14_225/0.12)]',
    dot: 'bg-accent-bright',
    ring: 'border-[oklch(0.72_0.14_225/0.25)]',
  },
  paused: {
    fg: 'text-warning',
    bg: 'bg-[oklch(0.78_0.16_75/0.12)]',
    dot: 'bg-warning',
    ring: 'border-[oklch(0.78_0.16_75/0.25)]',
  },
  pending: {
    fg: 'text-text-secondary',
    bg: 'bg-surface-elevated',
    dot: 'bg-text-tertiary',
    ring: 'border-border',
  },
  completed: {
    fg: 'text-success',
    bg: 'bg-[oklch(0.72_0.16_155/0.12)]',
    dot: 'bg-success',
    ring: 'border-[oklch(0.72_0.16_155/0.25)]',
  },
  failed: {
    fg: 'text-error',
    bg: 'bg-[oklch(0.65_0.21_28/0.12)]',
    dot: 'bg-error',
    ring: 'border-[oklch(0.65_0.21_28/0.25)]',
  },
  cancelled: {
    fg: 'text-text-tertiary',
    bg: 'bg-surface-elevated',
    dot: 'bg-text-tertiary',
    ring: 'border-border',
  },
  live: {
    fg: 'text-flame-hi',
    bg: 'bg-flame-bg-soft',
    dot: 'bg-flame',
    ring: 'border-flame/30',
  },
  active: {
    fg: 'text-accent-bright',
    bg: 'bg-[oklch(0.72_0.14_225/0.12)]',
    dot: 'bg-accent-bright',
    ring: 'border-[oklch(0.72_0.14_225/0.25)]',
  },
  draft: {
    fg: 'text-warning',
    bg: 'bg-[oklch(0.78_0.16_75/0.12)]',
    dot: 'bg-warning',
    ring: 'border-[oklch(0.78_0.16_75/0.25)]',
  },
  published: {
    fg: 'text-success',
    bg: 'bg-[oklch(0.72_0.16_155/0.12)]',
    dot: 'bg-success',
    ring: 'border-[oklch(0.72_0.16_155/0.25)]',
  },
  archived: {
    fg: 'text-text-tertiary',
    bg: 'bg-surface-elevated',
    dot: 'bg-text-tertiary',
    ring: 'border-border',
  },
};

export interface StatusPillProps extends React.ComponentProps<'span'> {
  status: StatusPillStatus;
  pulse?: boolean;
  children?: React.ReactNode;
}

export function StatusPill({
  status,
  pulse,
  children,
  className,
  ...rest
}: StatusPillProps): React.ReactElement {
  const c = STATUS_STYLES[status];
  return (
    <span
      data-status={status}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5',
        'font-mono text-[10.5px] font-semibold uppercase tracking-wide',
        c.fg,
        c.bg,
        c.ring,
        className
      )}
      {...rest}
    >
      <span
        className={cn(
          'inline-block size-1.5 rounded-full',
          c.dot,
          pulse && 'animate-[dup-pulse_1.6s_ease-out_infinite]'
        )}
      />
      {children ?? status}
    </span>
  );
}
